import { Box, Container, Typography, Grid, Card, CardContent, CardActionArea } from "@mui/material";
import { Link } from "react-router-dom";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HotelIcon from "@mui/icons-material/Hotel";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SpaIcon from "@mui/icons-material/Spa";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import SectionTitle from "../components/SectionTitle";

const PRESTATIONS = [
  { icon: HotelIcon, title: "Linge Hôtelier", description: "Lavage, repassage et pliage du linge de lit, serviettes et peignoirs pour hôtels et riads de luxe.", link: "/temoignages#hotel" },
  { icon: RestaurantIcon, title: "Linge Restaurant", description: "Entretien haut de gamme de vos nappes, serviettes et tabliers pour restaurants raffinés.", link: "/temoignages#restaurant" },
  { icon: SpaIcon, title: "Linge Spa", description: "Soin délicat du linge pour spas, hammams et centres de bien-être.", link: "/temoignages#spa" },
];

const PROCESS = [
  { number: "01", icon: DirectionsBikeIcon, title: "Collecte", description: "Nous récupérons votre linge directement chez vous à l'heure convenue." },
  { number: "02", icon: CleaningServicesIcon, title: "Traitement", description: "Lavage, détachage et repassage avec les meilleurs produits professionnels." },
  { number: "03", icon: CheckCircleOutlineIcon, title: "Contrôle", description: "Inspection qualité rigoureuse avant la remise du linge." },
  { number: "04", icon: LocalShippingIcon, title: "Livraison", description: "Retour rapide et ponctuel, emballé avec soin." },
];

const HeroBanner = () => (
  <Box sx={{
    position: "relative",
    height: { xs: "60vh", md: "80vh" },
    display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center",
    overflow: "hidden",
    background: 'url("/images/BanchisseriePage.jpg") center/cover no-repeat',
    "&::before": { content: '""', position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(10,6,2,0.6) 0%, rgba(10,6,2,0.4) 50%, rgba(10,6,2,0.85) 100%)", zIndex: 1 },
  }}>
    <Container maxWidth="md" sx={{ position: "relative", zIndex: 2, px: { xs: 2, md: 3 } }}>
      <Typography sx={{ color: "secondary.main", fontSize: "0.68rem", letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, mb: 1.5 }}>Services</Typography>
      <Typography variant="h1" sx={{ color: "#fff", fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: { xs: "1.6rem", md: "3rem" }, mb: 2, lineHeight: 1.1 }}>Blanchisserie de Luxe</Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 2.5 }}>
        <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.5)" }} />
        <Box sx={{ width: 5, height: 5, backgroundColor: "secondary.main", transform: "rotate(45deg)" }} />
        <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.5)" }} />
      </Box>
      <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: { xs: "0.85rem", md: "1rem" }, lineHeight: 1.8, maxWidth: 520, mx: "auto" }}>
        Entretien haut de gamme du linge pour hôtels, riads et restaurants à Marrakech.
      </Typography>
    </Container>
    <Box sx={{ position: "absolute", bottom: -1, left: 0, right: 0, zIndex: 3, lineHeight: 0 }}>
      <svg viewBox="0 0 1440 50" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 50 }}>
        <path d="M0,30 C360,55 1080,5 1440,30 L1440,50 L0,50 Z" fill="#f5f0e8" />
      </svg>
    </Box>
  </Box>
);

const PrestationCard = ({ icon: Icon, title, description, link }) => (
  <Card sx={{
    backgroundColor: "#fff", border: "1px solid rgba(201,168,76,0.2)", borderRadius: "12px",
    height: "100%", display: "flex", flexDirection: "column",
    boxShadow: "0 4px 20px rgba(26,17,8,0.08)", transition: "all 0.3s ease", overflow: "hidden",
    "&:hover": { transform: "translateY(-6px)", boxShadow: "0 16px 48px rgba(26,17,8,0.15)", borderColor: "secondary.main", "& .prestation-icon": { transform: "scale(1.1)", color: "secondary.main" } },
  }}>
    <CardActionArea component={Link} to={link} sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}>
      <Box sx={{ height: { xs: 140, md: 170 }, flexShrink: 0, backgroundColor: "#1c1208", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", "&::before": { content: '""', position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(201,168,76,0.15) 0%, transparent 70%)" } }}>
        <Icon className="prestation-icon" sx={{ fontSize: { xs: 70, md: 90 }, color: "rgba(201,168,76,0.85)", position: "relative", zIndex: 1, transition: "all 0.3s ease", filter: "drop-shadow(0 0 16px rgba(201,168,76,0.4))" }} />
      </Box>
      <Box sx={{ height: "3px", background: "linear-gradient(to right, transparent, rgba(201,168,76,0.5), transparent)" }} />
      <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1, display: "flex", flexDirection: "column", textAlign: "center" }}>
        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: { xs: "0.95rem", md: "1.1rem" }, color: "text.primary", mb: 1 }}>{title}</Typography>
        <Box sx={{ width: 30, height: "1px", backgroundColor: "rgba(201,168,76,0.4)", mx: "auto", mb: 1.5 }} />
        <Typography sx={{ color: "text.secondary", fontSize: { xs: "0.82rem", md: "0.87rem" }, lineHeight: 1.8, flexGrow: 1 }}>{description}</Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

const ProcessStep = ({ number, icon: Icon, title, description }) => (
  <Box sx={{ textAlign: "center", px: { xs: 1, md: 2 } }}>
    <Box sx={{ width: { xs: 55, md: 70 }, height: { xs: 55, md: 70 }, borderRadius: "50%", border: "1px solid rgba(201,168,76,0.35)", backgroundColor: "rgba(201,168,76,0.08)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
      <Icon sx={{ fontSize: { xs: 22, md: 30 }, color: "secondary.main" }} />
    </Box>
    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "0.7rem", color: "secondary.main", letterSpacing: "0.2em", mb: 1 }}>{number}</Typography>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1.5 }}>
      <Box sx={{ width: 20, height: "1px", backgroundColor: "rgba(201,168,76,0.3)" }} />
      <Box sx={{ width: 4, height: 4, backgroundColor: "secondary.main", transform: "rotate(45deg)" }} />
      <Box sx={{ width: 20, height: "1px", backgroundColor: "rgba(201,168,76,0.3)" }} />
    </Box>
    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: { xs: "0.88rem", md: "1rem" }, color: "text.primary", mb: 1 }}>{title}</Typography>
    <Typography sx={{ color: "text.secondary", fontSize: { xs: "0.78rem", md: "0.85rem" }, lineHeight: 1.7, maxWidth: 180, mx: "auto" }}>{description}</Typography>
  </Box>
);

const CollecteBanner = () => (
  <Box sx={{ position: "relative", overflow: "hidden", py: { xs: 6, md: 10 }, background: 'url("/images/bedroom-1.jpg") center/cover no-repeat', "&::before": { content: '""', position: "absolute", inset: 0, background: "rgba(10,6,2,0.78)", zIndex: 1 } }}>
    <Container maxWidth="md" sx={{ position: "relative", zIndex: 2, textAlign: "center", px: { xs: 2, md: 3 } }}>
      <LocalShippingIcon sx={{ fontSize: { xs: 28, md: 36 }, color: "secondary.main", mb: 2 }} />
      <Typography variant="h3" sx={{ color: "#fff", fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: { xs: "1.3rem", md: "2.2rem" }, mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>Service Collecte & Livraison</Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 3 }}>
        <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.5)" }} />
        <Box sx={{ width: 5, height: 5, backgroundColor: "secondary.main", transform: "rotate(45deg)" }} />
        <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.5)" }} />
      </Box>
      <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: { xs: "0.85rem", md: "1.05rem" }, lineHeight: 1.9, maxWidth: 500, mx: "auto" }}>
        Ramassage et livraison flexibles et rapides pour répondre aux besoins professionnels avec ponctualité et efficacité.
      </Typography>
    </Container>
  </Box>
);

const BlanchisseriePage = () => (
  <Box>
    <HeroBanner />
    <Box sx={{ position: "relative", backgroundColor: "#f5f0e8", "&::before": { content: '""', position: "absolute", inset: 0, background: 'url("/images/flowers.jpg") center/cover no-repeat', opacity: 0.03, pointerEvents: "none", zIndex: 0 } }}>

      {/* ── Nos Prestations — 3 cards side by side on desktop, stacked on mobile ── */}
      <Box sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 5, md: 8 }, position: "relative", zIndex: 1 }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
          <SectionTitle overline="Nos Prestations" title="Ce Que Nous Faisons" align="center" />
          <Box sx={{ display: "flex", gap: { xs: 2, md: 3 }, flexWrap: { xs: "wrap", md: "nowrap" } }}>
            {PRESTATIONS.map((p) => (
              <Box key={p.title} sx={{ flex: { xs: "1 1 100%", md: "1 1 0" }, minWidth: 0 }}>
                <PrestationCard {...p} />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Notre Processus ── */}
      <Box sx={{ py: { xs: 5, md: 8 }, position: "relative", zIndex: 1, borderTop: "1px solid rgba(201,168,76,0.15)" }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
          <SectionTitle overline="Comment ça marche" title="Notre Processus" align="center" />
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {PROCESS.map((step) => (
              <Grid item xs={6} md={3} key={step.number}>
                <ProcessStep {...step} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

    </Box>
    <CollecteBanner />
  </Box>
);

export default BlanchisseriePage;